import { RfqTemplateType } from '@lune-fe/dapi-models'
import { AxiosError } from 'axios'
import { Buffer as ExcelJSBuffer, Workbook } from 'exceljs'
import { Err, Ok, Result } from 'ts-results-es'

import { notify } from 'bugsnag'
import { uploadDataSheet } from 'endpoints/dapi'
import { FileDetails } from 'views/CalculateEmissions/RFQ/RFQ'
import {
    convertCellToString,
    CsvSheet,
    csvToTable,
    extractRowValues,
    MappingDataSheet,
    RowNumber,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    AutoFillEntryWithUpdate,
    LegAutoFillWithUpdate,
    RFQTemplateState,
} from 'views/CalculateEmissions/RFQ/utils/types'
import { isLegacySheet, updateBothAutoFill } from 'views/CalculateEmissions/RFQ/utils/utils'

import { MAX_SIZE_HUMAN_READABLE } from '../../Shared/CsvSheetCommon'
import { DataSheetJobSettings } from '../modals/NewCalculation'

export type Info = {
    subject: string[]
    description?: string
}

export function generateUnknownErrorInfo(): Info {
    return {
        subject: ['Unexpected upload failure'],
        description: 'Please contact customer support if this keeps happening.',
    }
}

function toError(error: unknown): Error {
    if (error instanceof Error) {
        return error
    } else {
        return new Error(`${error}`)
    }
}

function handleUnknownError(err: unknown): { result: 'error'; info: Info } {
    if (err instanceof Error) {
        notify(err)
        return {
            result: 'error',
            info: generateUnknownErrorInfo(),
        }
    }
    throw err
}

export async function uploadRfqFile(
    dataSheet: { workbook: Workbook } | { csvFile: File },
    uploadOptions: {
        mappingId: string
        jobSettings?: DataSheetJobSettings
    },
): Promise<{ result: 'error' | 'success'; info: Info }> {
    const formData = new FormData()
    const { jobSettings, mappingId } = uploadOptions
    if (!jobSettings && 'csvFile' in dataSheet) {
        return {
            result: 'error',
            info: { subject: ['Upload failed', 'Please provide upload setting options.'] },
        }
    }

    const buildClientAccountOptions = (() => {
        if (!jobSettings) return {}
        const { targetClientAccount } = jobSettings
        switch (targetClientAccount.type) {
            case 'existing':
                return { client_account: { id: targetClientAccount.id } }
            case 'create_new':
                return {
                    client_account: {
                        name: targetClientAccount.name,
                        currency: targetClientAccount.currency,
                        sustainability_page_visibility: targetClientAccount.privacy,
                    },
                }
            case 'per_row':
                return { per_row_client_account: true }
            case 'none':
                return {}
        }
    })()

    const jobOptions = {
        template_id: mappingId,
        // If job setting is not defined we know the file is a workbook, therefore it's a RFQ and is_shipment is false
        is_shipment: jobSettings?.shipmentsComplete ?? false,
        ...buildClientAccountOptions,
    }
    let uploadPath: 'rfq' | 'logistics-user-template' = 'rfq'
    if ('workbook' in dataSheet) {
        const { workbook } = dataSheet
        let buffer: ExcelJSBuffer
        try {
            buffer = await workbook.xlsx.writeBuffer()
        } catch (err) {
            // Our Nippon example RFQ file has issues with the library. This happens on a hidden sheet due
            // to what I believe are conditional formatting cell(s) that aren't correctly formatted. Excel still
            // allows them but trying to create a buffer via the library throws an exception. Sample stacktrace:
            //
            // cf-rule-xform.js:158  Uncaught (in promise) TypeError: Cannot read properties of undefined (reading '0')
            //    at d.renderExpression (cf-rule-xform.js:158:47)
            //    at d.render (cf-rule-xform.js:122:14)
            //    at conditional-formatting-xform.js:29:25
            //    at Array.forEach (<anonymous>)
            //    at t.exports.render (conditional-formatting-xform.js:26:17)
            //    at conditional-formattings-xform.js:41:20
            //    at Array.forEach (<anonymous>)
            //    at t.exports.render (conditional-formattings-xform.js:40:11)
            //    at j.render (worksheet-xform.js:330:36)
            //    at xlsx.js:564:22
            //
            // A proper fix to this is needed/wanted, but considering that:
            // - This is a critical xlsx to process
            // - We don't really need conditional formatting to be supported
            // - The affected sheet is hidden
            //
            // Our approach here is to remove all conditional formatting for hidden sheets if we detect this issue.
            //
            // This seems to be a recurring problem with the library, there are multiple closed and some open issues for
            // conditional formatting edge cases (and similar).
            //
            // On their README they state: "at present, only a subset of conditional formatting rules are supported."
            if (
                err instanceof TypeError &&
                err.message === "Cannot read properties of undefined (reading '0')"
            ) {
                // Remove conditional formatting from hidden sheets and try again
                workbook.eachSheet((worksheet) => {
                    if (worksheet.state === 'hidden' || worksheet.state === 'veryHidden') {
                        worksheet.removeConditionalFormatting({})
                    }
                })
                try {
                    buffer = await workbook.xlsx.writeBuffer()
                } catch (err) {
                    return handleUnknownError(err)
                }
            } else {
                return handleUnknownError(err)
            }
        }
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const file = new File([blob], 'sheet.xlsx', { type: blob.type })
        formData.append('sheet', file)
    } else {
        formData.append('sheet', dataSheet.csvFile)
        uploadPath = 'logistics-user-template'
    }
    formData.append('options', JSON.stringify(jobOptions))

    return uploadDataSheet(formData, uploadPath)
        .then(() => {
            return {
                result: 'success' as const,
                info: {
                    subject: [
                        `Upload complete.`,
                        `Processing your file, we’ll email you when ready.`,
                    ],
                    description: 'Should take a few minutes',
                },
            }
        })
        .catch((err) => {
            if (err instanceof AxiosError) {
                if (err.response?.status === 400) {
                    if (err.response.data.error.error_code === 'invalid_file_extension') {
                        return {
                            result: 'error',
                            info: {
                                subject: ['Upload failed', 'Please upload a .XLSX or .CSV file'],
                            },
                        }
                    }
                    if (err.response.data.error.error_code === 'max_file_size_exceeded') {
                        return {
                            result: 'error',
                            info: {
                                subject: [
                                    `Upload failed', 'Max file size of ${MAX_SIZE_HUMAN_READABLE} exceeded`,
                                ],
                            },
                        }
                    }
                    if (err.response.data.error.error_code === 'file_format_invalid') {
                        return {
                            result: 'error',
                            info: {
                                subject: [
                                    'Upload failed',
                                    'File format is invalid. Please upload a XLSX or CSV file.',
                                ],
                            },
                        }
                    }
                }
            }

            return handleUnknownError(err)
        })
}

export function encodeToBase64(input: ArrayBuffer): string {
    return Buffer.from(new Uint8Array(input)).toString('base64')
}

export function decodeFromBase64(base64: string): ArrayBuffer {
    return new Uint8Array(Buffer.from(base64, 'base64')).buffer
}

/**
 * Verifies a CSV can be parsed and is possible to be uploaded. Returns the type of
 * operation that is to be done: `legacy_processor` means the file can be used with
 * the legacy logistic processor, `new_processor` means a template is required. If
 * the file is not valid, an error is returned
 */
export async function validateCsv(
    file: File,
): Promise<
    Result<{ type: 'legacy_processor' } | { type: 'new_processor'; base64Data: string }, Error>
> {
    // TODO detect if can use the logistics v2 processor for files that support it.
    const arrayBuffer = await file.arrayBuffer()
    const table = await csvToTable(new Uint8Array(arrayBuffer), 0)
    if (table.isErr()) {
        return Err(new Error(table.error))
    }
    if (isLegacySheet(table.value)) {
        return Ok({ type: 'legacy_processor' })
    } else {
        return Ok({ type: 'new_processor', base64Data: encodeToBase64(arrayBuffer) })
    }
}

/**
 * Verifies a workbook can be parsed and is possible to be uploaded. Returns the parsed workbook alongside the data encoded in Base64
 */
export async function validateWorkbook(
    data: ArrayBuffer,
): Promise<
    Result<{ workbook: Workbook; base64Data: string }, { type: 'invalid_file'; info: Error }>
> {
    const workbook = new Workbook()
    let bufferData: ExcelJSBuffer
    try {
        await workbook.xlsx.load(data)
        // Write/serialise the workbook since it's a requirement for upload and it helps to store in session storage. This
        // operation is known to have issues so it also functions as a validation.
        bufferData = await workbook.xlsx.writeBuffer()
        return Ok({ workbook, base64Data: encodeToBase64(bufferData) })
    } catch (err) {
        // Our Nippon example RFQ file has issues with the library. This happens on a hidden sheet due
        // to what I believe are conditional formatting cell(s) that aren't correctly formatted. Excel still
        // allows them but trying to create a buffer via the library throws an exception. Sample stacktrace:
        //
        // cf-rule-xform.js:158  Uncaught (in promise) TypeError: Cannot read properties of undefined (reading '0')
        //    at d.renderExpression (cf-rule-xform.js:158:47)
        //    at d.render (cf-rule-xform.js:122:14)
        //    at conditional-formatting-xform.js:29:25
        //    at Array.forEach (<anonymous>)
        //    at t.exports.render (conditional-formatting-xform.js:26:17)
        //    at conditional-formattings-xform.js:41:20
        //    at Array.forEach (<anonymous>)
        //    at t.exports.render (conditional-formattings-xform.js:40:11)
        //    at j.render (worksheet-xform.js:330:36)
        //    at xlsx.js:564:22
        //
        // A proper fix to this is needed/wanted, but considering that:
        // - This is a critical xlsx to process
        // - We don't really need conditional formatting to be supported
        // - The affected sheet is hidden
        //
        // Our approach here is to remove all conditional formatting for hidden sheets if we detect this issue.
        //
        // This seems to be a recurring problem with the library, there are multiple closed and some open issues for
        // conditional formatting edge cases (and similar).
        //
        // On their README they state: "at present, only a subset of conditional formatting rules are supported."
        if (
            err instanceof TypeError &&
            err.message === "Cannot read properties of undefined (reading '0')"
        ) {
            // Remove conditional formatting from hidden sheets and try again
            workbook.eachSheet((worksheet) => {
                if (worksheet.state === 'hidden' || worksheet.state === 'veryHidden') {
                    worksheet.removeConditionalFormatting({})
                }
            })
            try {
                bufferData = await workbook.xlsx.writeBuffer()
                return Ok({ workbook, base64Data: encodeToBase64(bufferData) })
            } catch (err) {
                return Err({ type: 'invalid_file', info: toError(err) })
            }
        } else if (
            err instanceof TypeError &&
            err.message === "Cannot read properties of null (reading 'theme')" &&
            !Array.isArray(workbook.model.themes)
        ) {
            // Style in tables is not defined. We've seen this on LibreOffice files, it seems
            // a somewhat expected behaviour on their side. It feels the library should correctly
            // handle this since the actual style object can be empty, but it's set as `null` instead
            // of an empty object which makes accessing things error. Look for these cases and fix it.
            workbook.model.worksheets.forEach((sheet) => {
                // Internal types and actual data are not in sync for the library it seems so we
                // cannot use `getTables` and need to convert to `any` to iterate through the tables
                ;(sheet as any).tables?.forEach((table: any) => {
                    if (!table.style) {
                        table.style = {}
                    }
                })
            })

            bufferData = await workbook.xlsx.writeBuffer()
            return Ok({ workbook, base64Data: encodeToBase64(bufferData) })
        } else {
            return Err({ type: 'invalid_file', info: toError(err) })
        }
    }
}

/**
 * Load a workbook given a Base64 encoded data. No checks are done, only valid encoded data is expected
 */
export async function loadWorkbook(base64Data: string): Promise<Workbook> {
    const workbook = new Workbook()
    return await workbook.xlsx.load(decodeFromBase64(base64Data))
}

/**
 * Load a workbook given a Base64 encoded data of a CSV. No checks are done, only valid encoded data
 * is expected
 */
export async function loadCSV(
    base64Data: string,
    headerRow?: RowNumber,
): Promise<{ file: File; sheet?: MappingDataSheet }> {
    const csvData = decodeFromBase64(base64Data)
    const csv = new File([csvData], 'file.csv', { type: 'text/csv' })
    return { file: csv, sheet: headerRow ? await csvToSheet(csv, headerRow) : undefined }
}

export function generateWorkingData(
    sheet: MappingDataSheet,
    headerRowNumber: RowNumber,
    nrColumns: number,
): { headers: string[]; exampleRow: string[] } {
    return {
        headers: extractRowValues(sheet, headerRowNumber, nrColumns).map(
            (v) => convertCellToString(v, []) ?? '',
        ),
        exampleRow: extractRowValues(sheet, headerRowNumber.nextRow(), nrColumns).map(
            (v) => convertCellToString(v, []) ?? '',
        ),
    }
}

/**
 * Transforms a CSV into a MappingDataSheet which can be used as a `sheet` for processing.
 * Will throw an error if the csv is not valid.
 */
export async function csvToSheet(csv: File, headerRow: RowNumber): Promise<MappingDataSheet> {
    const arrayBuffer = await csv.arrayBuffer()
    // We subtract one to ensure that header row is included in the final sheet
    const startFrom = headerRow.toOneBased() - 1
    const tableWithHeader = (await csvToTable(new Uint8Array(arrayBuffer), startFrom)).unwrap()
    return new CsvSheet(tableWithHeader)
}

/**
 * Parses a datasheet data, returning the associated parsed data. This function expects a
 * valid datasheet to be given and will not make checks.
 */
export async function parseDatasheet(
    sheetType: RfqTemplateType,
    data: ArrayBuffer,
    sheetName: string,
    headerRow: RowNumber,
): Promise<
    | Pick<
          Extract<RFQTemplateState, { sheetType: 'rfq' }>,
          'sheetType' | 'workbook' | 'workbookBase64' | 'sheet'
      >
    | Pick<
          Extract<RFQTemplateState, { sheetType: 'logistics_user_template' }>,
          'sheetType' | 'workbook' | 'workbookBase64' | 'sheet'
      >
> {
    if (sheetType === 'rfq') {
        const buffer = Buffer.from(data) as unknown as ExcelJSBuffer
        const workbook = new Workbook()
        await workbook.xlsx.load(buffer)
        const sheet = workbook.getWorksheet(sheetName)!
        const workbookBase64 = encodeToBase64(buffer)
        return { sheetType, workbook, sheet, workbookBase64 }
    } else if (sheetType === 'logistics_user_template') {
        const csv = new File([data], 'file.csv', { type: 'text/csv' })
        const workbookBase64 = encodeToBase64(data)
        return { sheetType, workbook: csv, sheet: await csvToSheet(csv, headerRow), workbookBase64 }
    }

    throw new Error(`Unsupported RFQ sheet type: ${sheetType}`)
}

/**
 * Attempts to resolve a sheet given file details. If the file is a CSV the sheetName is not used
 */
export async function getMappingDataSheet(
    fileDetails: FileDetails,
    sheetName: string,
    headerRowNumber: RowNumber,
): Promise<MappingDataSheet | undefined> {
    return fileDetails.sheetType === 'rfq'
        ? fileDetails.data.getWorksheet(sheetName)
        : await csvToSheet(fileDetails.data, headerRowNumber)
}

export function resetAutoFill(autoFill: LegAutoFillWithUpdate['transportMode' | 'origin']): void {
    for (const groupType of Object.keys(autoFill) as Array<keyof typeof autoFill>) {
        Object.values(autoFill[groupType]).forEach((s) =>
            updateBothAutoFill(s as AutoFillEntryWithUpdate, false),
        )
    }
}

export function getMappingInfo(sheetType: RfqTemplateType): {
    title: string
    subTitle: string
    fileLabel: string
    acceptedFiles: { [k in string]: string[] }
} {
    return sheetType === 'rfq'
        ? {
              title: 'Tender calculations',
              subTitle: 'Upload excel data to process your tender',
              fileLabel: 'Excel',
              acceptedFiles: {
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                  'application/vnd.ms-excel': ['.xls'],
              },
          }
        : {
              title: 'Logistics spreadsheet calculations',
              subTitle: `Upload your logistics data by mapping your spreadsheet format once using our mapper tool or use EcoTransIT's CSV format directly.`,
              fileLabel: 'CSV',
              acceptedFiles: { 'text/csv': ['.csv'] },
          }
}
